<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useOrdersStore } from '@/store/orders'
import Progress from '@/components/forms/Progress.vue'
import { getFirstFieldError, normalizeApiError } from '@/utils/apiError'

const route = useRoute()
const router = useRouter()
const store = useOrdersStore()

const id = computed(() => route.params.id)
const isNew = computed(() => id.value === 'new')

const status = ref('')
const total = ref('')
const customer = ref('')
const comment = ref('')
const type = ref('')
const errorMessage = ref('')
const fieldErrors = ref({})

function clearFeedback() {
  errorMessage.value = ''
  fieldErrors.value = {}
}

function getFieldError(fieldName) {
  return getFirstFieldError(fieldErrors.value, fieldName)
}

function applyApiError(error, fallbackMessage) {
  const apiError = normalizeApiError(error, fallbackMessage ? { fallbackMessage } : {})
  errorMessage.value = apiError.message
  fieldErrors.value = apiError.fieldErrors
}

function resetForm() {
  status.value = ''
  total.value = ''
  customer.value = ''
  comment.value = ''
}

function fillForm(order = {}) {
  status.value = order.status || ''
  total.value = order.total ?? ''
  customer.value = order.customer?._id?.$oid || order.customer?._id || order.customer || ''
  comment.value = order.comment || ''
}

async function loadOrder() {
  clearFeedback()

  if (isNew.value) {
    resetForm()
    type.value = ''
    return
  }

  type.value = 'loading'
  try {
    await store.getOrder(id.value)
    fillForm(store.currentOrder || {})
    type.value = ''
  } catch (error) {
    type.value = 'error'
    applyApiError(error, 'Не вдалося завантажити замовлення.')
  }
}

async function updateOrder() {
  clearFeedback()

  if (isNew.value) {
    resetForm()
    type.value = 'success'
    return
  }

  type.value = 'loading'
  try {
    await store.getOrder(id.value)
    fillForm(store.currentOrder || {})
    type.value = 'success'
  } catch (error) {
    type.value = 'error'
    applyApiError(error, 'Не вдалося оновити дані форми.')
  }
}

async function saveOrder() {
  type.value = 'loading'
  clearFeedback()
  const sendData = {}

  if (status.value) sendData.status = status.value
  if (total.value !== '' && !Number.isNaN(Number(total.value))) sendData.total = Number(total.value)
  if (customer.value) sendData.customer = customer.value
  if (comment.value) sendData.comment = comment.value

  try {
    if (isNew.value) {
      const created = await store.addOrder(sendData)
      const createdId = created._id?.$oid || created._id || created.id
      type.value = 'success'
      if (createdId) {
        await router.replace(`/orders/${createdId}`)
      }
      return
    }

    await store.updateOrder(id.value, sendData)
    type.value = 'success'
  } catch (error) {
    type.value = 'error'
    applyApiError(error, 'Не вдалося зберегти замовлення.')
  }
}

onMounted(loadOrder)
watch(() => route.params.id, loadOrder)
</script>

<template>
  <div class="container mt-4">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h2>{{ route.meta.title }}</h2>
      <Progress :type="type" :error-message="errorMessage" />
      <div>
        <button class="btn btn-outline-secondary me-2" @click="updateOrder" :disabled="type === 'loading'">
          <i class="bi bi-arrow-clockwise"></i>
        </button>
        <button class="btn btn-primary" @click="saveOrder" :disabled="type === 'loading'">
          <i class="bi bi-save"></i>
        </button>
      </div>
    </div>

    <div class="card shadow-sm">
      <div class="card-body">
        <div class="row mb-3">
          <div class="col-md-4 d-flex justify-content-end align-items-center">Статус</div>
          <div class="col-md-8">
            <input v-model="status" type="text" class="form-control" :class="{ 'is-invalid': !!getFieldError('status') }" />
            <div v-if="getFieldError('status')" class="invalid-feedback d-block">
              {{ getFieldError('status') }}
            </div>
          </div>
        </div>
        <div class="row mb-3">
          <div class="col-md-4 d-flex justify-content-end align-items-center">Сума</div>
          <div class="col-md-8">
            <input v-model="total" type="number" step="0.01" class="form-control" :class="{ 'is-invalid': !!getFieldError('total') }" />
            <div v-if="getFieldError('total')" class="invalid-feedback d-block">
              {{ getFieldError('total') }}
            </div>
          </div>
        </div>
        <div class="row mb-3">
          <div class="col-md-4 d-flex justify-content-end align-items-center">Клієнт (ID)</div>
          <div class="col-md-8">
            <input v-model="customer" type="text" class="form-control" :class="{ 'is-invalid': !!getFieldError('customer') }" />
            <div v-if="getFieldError('customer')" class="invalid-feedback d-block">
              {{ getFieldError('customer') }}
            </div>
          </div>
        </div>
        <div class="row mb-3">
          <div class="col-md-4 d-flex justify-content-end align-items-center">Коментар</div>
          <div class="col-md-8">
            <textarea v-model="comment" class="form-control" rows="3" :class="{ 'is-invalid': !!getFieldError('comment') }"></textarea>
            <div v-if="getFieldError('comment')" class="invalid-feedback d-block">
              {{ getFieldError('comment') }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">

</style>
